import Big from "big.js";
import { cloneDeep, remove } from "lodash";
import OrderStatus from "@/enum/OrderStatus";
import OrderItemDTO from "@/dto/order/OrderItemDTO";

export interface EwaybillOrderItemDto {
    originalId: string;
    /**
     * 商品数量，负数表示分数，如-2表示1/2（半个商品），-3表示1/3（三分之一个商品）
     */
    quantity: number;
    status: OrderStatus;
}

export interface EwaybillOrderPackageDto {
    /**
     * 包裹内的商品信息
     */
    orderItems: EwaybillOrderItemDto[];
}

type Unit = {
    originalId: string;
    status: OrderStatus;
    weight: Big; // grams
};

type Bin = {
    orderItems: EwaybillOrderItemDto[];
    totalWeight: Big;
};

/**
 * 按总数量分包：每张快递单打印固定数量的商品
 * @param selectedOrderItems 选中的订单商品
 * @param specifiedSplitQuantity 每张快递单打印的商品总数量
 * @param enabledPrintGiftInLastWaybill 是否将赠品打印到最后一张面单
 * @returns 包裹数量和包裹列表
 */
export function splitByTotalQuantity(
    selectedOrderItems: OrderItemDTO[],
    specifiedSplitQuantity: number,
    enabledPrintGiftInLastWaybill: boolean,
): { packageCount: number; packages: EwaybillOrderPackageDto[] } {
    let packageCount = 0;
    let packages: EwaybillOrderPackageDto[] = [];
    // 克隆一份订单项，避免修改原数据
    const clonedOrderItems = cloneDeep(selectedOrderItems);
    let selectedGiftOrderItems: OrderItemDTO[] = [];

    if (enabledPrintGiftInLastWaybill) {
        selectedGiftOrderItems = remove(clonedOrderItems, (orderItem) => {
            return !!orderItem.isGift;
        });
    }

    // 计算所有商品的总数量
    const totalQuantity = clonedOrderItems.reduce((sum, item) => sum + item.quantity, 0);

    if (totalQuantity > 0) {
        let remainingQuantity = totalQuantity;

        // 按顺序分配商品到各个包裹
        while (remainingQuantity > 0) {
            const currentPackage: EwaybillOrderPackageDto = {
                orderItems: [],
            };

            let currentPackageQuantity = 0;

            // 遍历所有订单项，填充当前包裹
            for (let i = 0; i < clonedOrderItems.length && currentPackageQuantity < specifiedSplitQuantity; i++) {
                const orderItem = clonedOrderItems[i];

                if (orderItem.quantity <= 0) {
                    continue;
                }

                // 计算当前包裹还能放多少商品
                const availableSpace = specifiedSplitQuantity - currentPackageQuantity;

                if (orderItem.quantity <= availableSpace) {
                    // 当前商品可以全部放入当前包裹
                    currentPackage.orderItems.push({
                        originalId: orderItem.originalId,
                        quantity: orderItem.quantity,
                        status: orderItem.status,
                    });
                    currentPackageQuantity += orderItem.quantity;
                    remainingQuantity -= orderItem.quantity;
                    orderItem.quantity = 0;
                } else {
                    // 当前商品需要拆分，部分放入当前包裹
                    currentPackage.orderItems.push({
                        originalId: orderItem.originalId,
                        quantity: availableSpace,
                        status: orderItem.status,
                    });
                    currentPackageQuantity += availableSpace;
                    remainingQuantity -= availableSpace;
                    orderItem.quantity -= availableSpace;
                }
            }

            packages.push(currentPackage);
            packageCount++;
        }
    }

    // 如果设置了把赠品打印到最后一张面单
    if (enabledPrintGiftInLastWaybill && selectedGiftOrderItems.length) {
        // 如果有成功分包，则直接放到最后一个包裹中去
        if (packageCount) {
            const lastPackage = packages[packages.length - 1];

            selectedGiftOrderItems.forEach((it) => {
                lastPackage.orderItems.push({
                    originalId: it.originalId,
                    quantity: it.quantity,
                    status: it.status,
                });
            });
        } else {
            packageCount = 1;
            packages.push({
                orderItems: selectedGiftOrderItems.map((it) => {
                    return {
                        originalId: it.originalId,
                        quantity: it.quantity,
                        status: it.status,
                    };
                }),
            });
        }
    }

    /** 无进行过任何拆分（无orderItem的订单） */
    if (packageCount === 0) {
        packageCount = 1;
        packages = null;
    }

    return { packageCount, packages };
}

/**
 * 按重量分包：
 * - 当商品拆分后的最小单元数 < 15：使用DFS，保证包裹数最少
 * - 当 >= 15：使用FFD算法，保证性能 包裹数不一定是最少（不保证最优）
 *
 * @param selectedOrderItems 选中的订单商品
 * @param maxWeightPerPackage 每张快递单的最大重量（kg）
 * @param defaultUnitWeight 默认单件重量（kg）
 */
export function splitByWeight(
    selectedOrderItems: OrderItemDTO[],
    maxWeightPerPackage: number,
    defaultUnitWeight: number,
): { packageCount: number; packages: EwaybillOrderPackageDto[] | null } {
    const maxWeightGrams = new Big(maxWeightPerPackage).times(1000);

    // 拆分为最小单位
    const itemUnits: { originalId: string; status: OrderStatus; weight: Big }[] = [];

    selectedOrderItems.forEach((item) => {
        if (item.quantity <= 0) return;

        const singleWeight = item.weight ? new Big(item.weight) : new Big(defaultUnitWeight || 0).times(1000);

        for (let i = 0; i < item.quantity; i++) {
            itemUnits.push({
                originalId: item.originalId,
                status: item.status,
                weight: singleWeight,
            });
        }
    });

    if (itemUnits.length === 0) {
        return { packageCount: 1, packages: null };
    }

    itemUnits.sort((a, b) => b.weight.cmp(a.weight));

    let ret: {
        packageCount: number;
        packages: EwaybillOrderPackageDto[];
    };

    if (itemUnits.length < 15) {
        ret = exactSolve(itemUnits, maxWeightGrams);
    } else {
        ret = greedySolve(itemUnits, maxWeightGrams);
    }

    assertPackingValid(itemUnits, ret.packages);

    return ret;
}

export function exactSolve(
    itemUnits: Unit[],
    maxWeightGrams: Big,
): { packageCount: number; packages: EwaybillOrderPackageDto[] } {
    let bestBins: Bin[] | null = null;
    let bestCount = Infinity;

    itemUnits = [...itemUnits].sort((a, b) => b.weight.cmp(a.weight));

    function dfs(index: number, bins: Bin[]) {
        // 已有解更优
        if (bins.length >= bestCount) return;

        if (index === itemUnits.length) {
            bestCount = bins.length;
            bestBins = bins.map((b) => ({
                totalWeight: b.totalWeight,
                orderItems: JSON.parse(JSON.stringify(b.orderItems)),
            }));
            return;
        }

        const unit = itemUnits[index];

        // 尝试放入已有 bin中
        for (let i = 0; i < bins.length; i++) {
            const bin = bins[i];

            const newWeight = bin.totalWeight.plus(unit.weight);

            // 超重则跳过
            if (newWeight.gt(maxWeightGrams)) continue;

            // 放入
            const existing = bin.orderItems.find(
                (it) => it.originalId === unit.originalId && it.status === unit.status,
            );

            if (existing) {
                existing.quantity += 1;
            } else {
                bin.orderItems.push({
                    originalId: unit.originalId,
                    status: unit.status,
                    quantity: 1,
                });
            }

            const oldWeight = bin.totalWeight;
            bin.totalWeight = newWeight;

            dfs(index + 1, bins);

            // 回溯
            bin.totalWeight = oldWeight;

            if (existing) {
                existing.quantity -= 1;
                if (existing.quantity === 0) {
                    bin.orderItems = bin.orderItems.filter((x) => x !== existing);
                }
            } else {
                bin.orderItems.pop();
            }
        }

        // 新开 bin
        const newBin: Bin = {
            orderItems: [
                {
                    originalId: unit.originalId,
                    status: unit.status,
                    quantity: 1,
                },
            ],
            totalWeight: unit.weight,
        };

        bins.push(newBin);
        dfs(index + 1, bins);
        bins.pop();
    }

    dfs(0, []);

    if (!bestBins) {
        return { packageCount: 1, packages: null };
    }

    return {
        packageCount: bestBins.length,
        packages: bestBins.map((b) => ({
            orderItems: b.orderItems,
        })),
    };
}

export function greedySolve(
    itemUnits: { originalId: string; status: OrderStatus; weight: Big }[],
    maxWeightGrams: Big,
): { packageCount: number; packages: EwaybillOrderPackageDto[] } {
    const packagesWithWeight: { orderItems: EwaybillOrderItemDto[]; totalWeight: Big }[] = [];

    itemUnits.forEach((unit) => {
        // 找第一个能放下的包裹
        let foundPackage = packagesWithWeight.find((pkg) => pkg.totalWeight.plus(unit.weight).lte(maxWeightGrams));

        if (foundPackage) {
            const existingItem = foundPackage.orderItems.find(
                (it) => it.originalId === unit.originalId && it.status === unit.status,
            );

            if (existingItem) {
                existingItem.quantity += 1;
            } else {
                foundPackage.orderItems.push({
                    originalId: unit.originalId,
                    quantity: 1,
                    status: unit.status,
                });
            }

            foundPackage.totalWeight = foundPackage.totalWeight.plus(unit.weight);
        } else {
            // 新包裹
            packagesWithWeight.push({
                orderItems: [
                    {
                        originalId: unit.originalId,
                        quantity: 1,
                        status: unit.status,
                    },
                ],
                totalWeight: unit.weight,
            });
        }
    });

    return {
        packageCount: packagesWithWeight.length,
        packages: packagesWithWeight.map((pkg) => ({
            orderItems: pkg.orderItems,
        })),
    };
}

/**
 * 做一下风险兜底，避免产生与订单商品种类数不一致的情况
 */
function assertPackingValid(
    itemUnits: { originalId: string; status: OrderStatus }[],
    packages: { orderItems: { originalId: string; status: OrderStatus; quantity: number }[] }[],
) {
    if (!itemUnits?.length || !packages?.length) return;

    const input = new Map<string, number>();

    for (const u of itemUnits) {
        const k = `${u.originalId}|${u.status}`;
        input.set(k, (input.get(k) || 0) + 1);
    }

    const output = new Map<string, number>();

    for (const p of packages) {
        for (const i of p.orderItems) {
            const k = `${i.originalId}|${i.status}`;
            output.set(k, (output.get(k) || 0) + i.quantity);
        }
    }

    for (const [k, v] of input) {
        if ((output.get(k) || 0) !== v) {
            throw new Error(`Packing mismatch: ${k}`);
        }
    }

    for (const [k] of output) {
        if (!input.has(k)) {
            throw new Error(`Unexpected item in output: ${k}`);
        }
    }
}
